import * as Sentry from '@sentry/react';
import { AllowedReducer, Constants } from '@leverege/api-redux';
import I18N from '@leverege/i18n';
import Molten from '@leverege/molten';
import { Context } from '@leverege/plugin';
import { Toast } from '@leverege/ui-elements';
import { GlobalState } from '@leverege/ui-redux';
import { canCancelTagRingCommand } from './Util.js';
import { amplitudeRingTagTrackEvent, getRingTagEventSource } from '../../tracking/AmplitudeRingTagTrackingEvent.js';
const {
  t,
  tIcon
} = I18N.ns('recovr.details.findAsset.FindAssetCancelAction');
const ACTION = 'ringTag';
export default {
  id: 'recovr.findAsset.FindAssetCancelAction',
  name: 'Find Asset',
  matches: [{
    client: ['DataCardDetail'],
    use: 'actionBar',
    objectType: 'assetTags'
  }],
  isPrepared: context => {
    const targets = Context.getTargetsOfType(context, 'assetTags');
    if (targets == null || targets.length === 0 || targets.length > 1) {
      return true;
    }
    const asset = GlobalState.get().models.lookup?.[targets[0].id]; // We have to pull the item from state, given that in the case of the selection, the item remains stale.
    const tag = asset.data.tag;
    if (tag == null) {
      return true;
    }
    const orgId = tag.data?.organization?.id;
    const companyId = tag.data?.company?.id;
    const locationId = tag.data?.location?.id;

    // It isn't an actual resourceId, but something closer to a ref.
    // Permissioned role will always be set at the company or location level.
    const resourceId = [orgId, companyId, locationId].filter(x => x != null).join('/');
    if (resourceId.length === 0) {
      return true;
    }
    const a = AllowedReducer.get(GlobalState.get(), resourceId);
    return a[ACTION] != null;
  },
  prepare: async context => {
    const targets = Context.getTargetsOfType(context, 'assetTags');
    const asset = GlobalState.get().models.lookup?.[targets[0].id]; // We have to pull the item from state, given that in the case of the selection, the item remains stale.
    const tag = asset.data.tag;
    const tagId = tag.id;
    const orgId = tag.data?.organization?.id;
    const companyId = tag.data?.company?.id;
    const locationId = tag.data?.location?.id;

    // It isn't an actual resourceId, but something closer to a ref.
    // Permissioned role will always be set at the company or location level.
    const resourceId = [orgId, companyId, locationId].filter(x => x != null).join('/');
    const a = AllowedReducer.get(GlobalState.get(), resourceId);
    if (a[ACTION] != null) {
      return a[ACTION];
    }
    return GlobalState.dispatch(async (dispatch, getState, {
      api
    }) => {
      let nApi;
      if (orgId && companyId && locationId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'organization').obj(orgId).grp('companies').obj(companyId).grp('locations').obj(locationId).grp('tags').obj(`${tagId}`);
      } else if (companyId && locationId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('locations').obj(locationId).grp('tags') // Has to be kept, interface service route only exists for the tag blueprint type.
        .obj(tagId);
      } else if (companyId) {
        nApi = api.interface(process.env.SYSTEM_ID, 'company').obj(companyId).grp('tags') // Has to be kept, interface service route only exists for the tag blueprint type.
        .obj(tagId);
      } else {
        nApi = api.interface(process.env.SYSTEM_ID, 'location').obj(locationId).grp('tags') // Has to be kept, interface service route only exists for the tag blueprint type.
        .obj(tagId);
      }
      const res = await nApi.srv('kudelskidevice').post(`device/${tagId}/command`, null, {
        test: true
      });
      dispatch({
        type: Constants.ALLOWED_SET,
        resourceId,
        action: ACTION,
        allowed: res.allowed === true
      });
      return res.allowed;
    });
  },
  handles: context => {
    const targets = Context.getTargetsOfType(context, 'assetTags');
    if (targets == null || targets.length === 0 || targets.length > 1) {
      return false;
    }
    const asset = GlobalState.get().models.lookup?.[targets[0].id]; // We have to pull the item from state, given that in the case of the selection, the item remains stale.
    const tag = asset.data.tag;
    if (tag == null) {
      return false;
    }
    const orgId = tag.data?.organization?.id;
    const companyId = tag.data?.company?.id;
    const locationId = tag.data?.location?.id;

    // It isn't an actual resourceId, but something closer to a ref.
    // Permissioned role will always be set at the company or location level.
    const resourceId = [orgId, companyId, locationId].filter(x => x != null).join('/');
    if (resourceId.length === 0) {
      return false;
    }
    const a = AllowedReducer.get(GlobalState.get(), resourceId);
    return a[ACTION] === true && canCancelTagRingCommand(tag);
  },
  appearance: ({
    context
  }) => {
    return {
      name: t('name'),
      icon: tIcon('icon')
    };
  },
  perform: ({
    context
  }) => {
    const onCommand = context.onCommand;
    const targets = Context.getTargetsOfType(context, 'assetTags');
    const asset = GlobalState.get().models.lookup?.[targets[0].id]; // We have to pull the item from state, given that in the case of the selection, the item remains stale.
    const tag = asset.data.tag;
    GlobalState.dispatch(async (dispatch, getState, {
      api
    }) => {
      try {
        const systemId = Molten.getConfig('api/systemId');
        const tagId = tag.id;
        const orgId = tag.data?.organization?.id;
        const companyId = tag.data?.company?.id;
        const locationId = tag.data?.location?.id;
        let nApi;
        if (orgId && companyId && locationId) {
          nApi = api.interface(systemId, 'organization').obj(orgId).grp('companies').obj(companyId).grp('locations').obj(locationId).grp('tags').obj(`${tagId}`);
        } else if (companyId && locationId) {
          nApi = api.interface(systemId, 'company').obj(companyId).grp('locations').obj(locationId).grp('tags').obj(`${tagId}`);
        } else if (companyId) {
          nApi = api.interface(systemId, 'company').obj(companyId).grp('tags').obj(tagId);
        } else {
          nApi = api.interface(systemId, 'location').obj(locationId).grp('tags').obj(tagId);
        }
        await nApi.srv('kudelskidevice').post(`device/${tagId}/command`, {
          command: {
            type: 'peripheralDevice.cancel.ring'
          }
        }, {});

        // Initiate tag device data subscription to correctly mirror the state of the tag
        // in the UI without waiting for periodic refreshes.
        onCommand();

        /** AMPLITUDE TRACKING CODE */
        const screen = context?.clientProps?.client;
        const source = getRingTagEventSource(screen);
        amplitudeRingTagTrackEvent(source, 'cancel');
        /** END OF AMPLITUDE TRACKING CODE */

        Toast.success(t('success'));
      } catch (err) {
        Sentry.captureException(err);
        console.error(err);
        Toast.error([t('error'), err?.message]);
      }
    });
  }
};